import React, { useEffect, useRef, useState } from 'react';
import api from '../lib/api';
import CornerPanel from '../components/CornerPanel';
import RefreshButton from '../components/RefreshButton';
import useVisibilityAwareInterval from '../hooks/useVisibilityAwareInterval';
import { RefreshCw, TrendingUp, ShieldOff, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import MobileSheet from '../components/MobileSheet';

const gradeColor = (g) => ({ 'A+': 'text-buy', A: 'text-buy', B: 'text-hold', C: 'text-[var(--txt-secondary)]' }[g] || 'text-[var(--txt-secondary)]');

// Iter 88 — momentum lifecycle badge styling. Each backend stage maps
// to a colour + short display glyph. Longer full-label + reason list
// shows in tooltip so the table stays scannable.
const LIFECYCLE_STYLE = {
  early_up:     { color: 'text-buy',                   border: 'border-buy',                   glyph: '↗ EARLY',   short: 'early'        },
  accelerating: { color: 'text-buy',                   border: 'border-buy',                   glyph: '⇈ ACCEL',   short: 'accelerating' },
  peak:         { color: 'text-hold',                  border: 'border-hold',                  glyph: '▲ PEAK',    short: 'peak'         },
  slowing:      { color: 'text-hold',                  border: 'border-hold/60',               glyph: '→ SLOW',    short: 'slowing'      },
  flat:         { color: 'text-[var(--txt-secondary)]',border: 'border-[var(--border-default)]',glyph: '· FLAT',    short: 'flat'         },
  fading:       { color: 'text-sell',                  border: 'border-sell/60',               glyph: '↘ FADE',    short: 'fading'       },
  reversed:     { color: 'text-sell',                  border: 'border-sell',                  glyph: '↓ REV',     short: 'reversed'     },
  unknown:      { color: 'text-[var(--txt-muted)]',    border: 'border-[var(--border-muted)]', glyph: '— N/A',     short: 'n/a'          },
};

// Iter 85 code-review — VW-ROC (volume-weighted rate-of-change) tuning.
// These are the thresholds the momentum score uses to convert the
// backend's normalised sub_scores.volume and sub_scores.momentum
// (already 0-40-ish scale) into a discrete ±5 tilt each. Hoisted so the
// operator can tune from one place if the momentum ranking drifts.
const VW_VOL_HOT   = 5;    // volume sub-score at/above → +5 (strong expansion)
const VW_VOL_COLD  = 1;    // volume sub-score at/below → -5 (volume drying up)
const VW_MOM_HOT   = 30;   // momentum sub-score at/above → +5 (RSI+MACD tailwind)
const VW_MOM_COLD  = 15;   // momentum sub-score at/below → -5 (momentum fading)

export default function Discovery() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('ALL');
  // Iter 88 — separate lifecycle filter so operator can hunt by
  // momentum stage (e.g. "show me every early_up on the A+ list").
  const [stageFilter, setStageFilter] = useState('ALL');
  const [universeSize, setUniverseSize] = useState(0);
  const [last, setLast] = useState(null);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [cachedAge, setCachedAge] = useState(null);
  const [warming, setWarming] = useState(false);
  const [warmCoverage, setWarmCoverage] = useState(0);
  const [, forceTick] = useState(0);
  // Mobile filter sheet — desktop keeps chips inline, phones use a modal
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const lastErrAt = useRef(0);

  // Tick once per second while in cooldown so the countdown stays live.
  useEffect(() => {
    if (cooldownUntil <= Date.now()) return;
    const t = setInterval(() => {
      if (cooldownUntil <= Date.now()) { setCooldownUntil(0); }
      forceTick((x) => x + 1);
    }, 1000);
    return () => clearInterval(t);
  }, [cooldownUntil]);

  const run = async (manual = false) => {
    // Hard guard: while in cooldown, never even attempt — manual or auto.
    if (Date.now() < cooldownUntil) {
      if (manual) {
        const s = Math.ceil((cooldownUntil - Date.now()) / 1000);
        toast.error(`Scan throttled · retry in ${s}s`);
      }
      return;
    }
    // Back-off: if the last automatic scan failed within 90s, skip this one.
    if (!manual && Date.now() - lastErrAt.current < 90000) return;
    setLoading(true);
    try {
      // Manual click bypasses the server-side 60s cache for a fresh scan.
      const url = manual ? '/discovery/scan?top_n=30&refresh=true' : '/discovery/scan?top_n=30';
      const { data } = await api.get(url);
      setRows(data.rows || []);
      setUniverseSize(data.universe_size || 0);
      setLast(new Date().toISOString());
      setCachedAge(data.cached ? data.cache_age_s : null);
      setWarming(!!data.warming);
      setWarmCoverage(data.cache_coverage_pct || 0);
      if (manual) {
        const w = data.warming ? ` · warming ${data.cache_coverage_pct}%` : '';
        toast.success(`Scanned ${data.universe_size} tickers${data.cached ? ' (cached)' : ''}${w}`);
      }
    } catch (e) {
      lastErrAt.current = Date.now();
      if (e?.response?.status === 429) {
        // Server provided Retry-After. Honor it precisely.
        const retry = Number(e.response.data?.retry_after_s)
          || Number(e.response.headers?.['retry-after'])
          || 60;
        setCooldownUntil(Date.now() + retry * 1000);
        if (manual) {
          toast.error(`Scan rate-limited · retry in ${retry}s`);
        }
      } else if (manual) {
        toast.error(e?.response?.data?.detail || 'Scan failed');
      }
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { run(); }, []);
  // Discovery scan is heavy + Polygon rate-limits; bump to 5-min interval.
  // Phase 50.S: while warming, refresh every 60s to surface new listings.
  useVisibilityAwareInterval(run, warming ? 60000 : 300000, [warming]);

  const filtered = (filter === 'ALL' ? rows : rows.filter((r) => r.grade === filter))
    .filter((r) => stageFilter === 'ALL' || (r.lifecycle?.stage) === stageFilter);
  const cooldownLeft = Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));

  const activeFilterCount =
    (filter !== 'ALL' ? 1 : 0) + (stageFilter !== 'ALL' ? 1 : 0);

  // Filter chips — used inside the mobile bottom-sheet. Desktop
  // renders its own copies inline (see below) with the primary
  // testids so existing tests keep passing.
  const filterChipsSheet = (
    <div className="flex flex-col gap-4">
      <div>
        <div className="text-[9px] tracking-[0.3em] uppercase text-[var(--txt-muted)] mb-2">grade</div>
        <div className="flex flex-wrap gap-2">
          {['ALL', 'A+', 'A', 'B', 'C'].map((g) => (
            <button
              key={g}
              data-testid={`discovery-filter-sheet-${g}`}
              onClick={() => setFilter(g)}
              className={`text-[11px] font-display tracking-[0.3em] uppercase px-3 py-2 border transition-colors min-h-[40px]
                ${filter === g ? 'bg-cyan-sys text-black border-cyan-sys' : 'text-cyan-sys border-[var(--border-muted)]'}`}
            >
              {g}
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="text-[9px] tracking-[0.3em] uppercase text-[var(--txt-muted)] mb-2">stage</div>
        <div className="flex flex-wrap gap-2">
          {[
            ['ALL',           'all',           'text-cyan-sys'],
            ['early_up',      '↗ early',      'text-buy'],
            ['accelerating',  '⇈ accel',      'text-buy'],
            ['peak',          '▲ peak',       'text-hold'],
            ['slowing',       '→ slow',       'text-hold'],
            ['flat',          '· flat',       'text-[var(--txt-secondary)]'],
            ['fading',        '↘ fade',       'text-sell'],
            ['reversed',      '↓ rev',        'text-sell'],
          ].map(([key, label, color]) => (
            <button
              key={key}
              data-testid={`discovery-stage-filter-sheet-${key}`}
              onClick={() => setStageFilter(key)}
              className={`text-[10px] font-mono tracking-widest uppercase px-3 py-2 border transition-colors min-h-[40px]
                ${stageFilter === key
                  ? 'bg-cyan-sys/10 border-cyan-sys ' + color
                  : color + ' border-[var(--border-muted)]'}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <button
        data-testid="discovery-filter-sheet-clear"
        onClick={() => { setFilter('ALL'); setStageFilter('ALL'); }}
        className="mt-2 text-[10px] tracking-widest uppercase border border-[var(--border-muted)] text-[var(--txt-secondary)] hover:border-sell hover:text-sell px-3 py-2 min-h-[40px]"
      >
        clear all filters
      </button>
    </div>
  );

  return (
    <div className="p-4 space-y-3">
      <CornerPanel
        title="Stock Discovery Engine"
        right={
          <div className="flex items-center gap-2">
            <span className="text-cyan-sys">universe: {universeSize} tickers</span>
            <RefreshButton onRefresh={() => run(true)} lastUpdated={last} testId="discovery-refresh" />
          </div>
        }
        testId="discovery-panel"
      >
        <div className="flex flex-col gap-2 mb-3">
          <div className="flex items-center justify-between gap-3">
            {/* Desktop grade filter chips inline */}
            <div className="hidden md:flex gap-1">
              {['ALL', 'A+', 'A', 'B', 'C'].map((g) => (
                <button
                  key={g}
                  data-testid={`discovery-filter-${g}`}
                  onClick={() => setFilter(g)}
                  className={`text-[10px] font-display tracking-[0.3em] uppercase px-3 py-1 border transition-colors
                    ${filter === g ? 'bg-cyan-sys text-black border-cyan-sys' : 'text-cyan-sys border-[var(--border-muted)] hover:border-cyan-sys'}`}
                >
                  {g}
                </button>
              ))}
            </div>
            {/* Mobile filter button — opens sheet */}
            <button
              data-testid="discovery-mobile-filter-btn"
              onClick={() => setMobileFiltersOpen(true)}
              className="md:hidden inline-flex items-center gap-2 text-[11px] font-display tracking-[0.3em] uppercase border border-cyan-sys text-cyan-sys hover:bg-cyan-sys hover:text-black px-3 py-1.5 transition-colors min-h-[36px]"
            >
              <SlidersHorizontal size={12} /> filters
              {activeFilterCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[9px] px-1 bg-cyan-sys text-black rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </button>
            <button
              data-testid="discovery-scan-btn"
              onClick={() => run(true)}
              disabled={loading || cooldownLeft > 0}
              className={`flex items-center gap-2 text-[11px] font-display tracking-[0.3em] uppercase border px-3 py-1.5 transition-colors min-h-[36px]
                ${cooldownLeft > 0
                  ? 'border-[var(--border-muted)] text-[var(--txt-muted)] cursor-not-allowed'
                : 'border-buy text-buy hover:bg-buy hover:text-black'}`}
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            {cooldownLeft > 0
              ? `throttled · ${cooldownLeft}s`
              : loading
                ? 'scanning...'
                : 'run scan'}
          </button>
          </div>
          {/* Desktop-only stage filter chips */}
          <div className="hidden md:flex items-center gap-2 flex-wrap" data-testid="discovery-stage-filter-row">
            <span className="text-[9px] tracking-[0.3em] uppercase text-[var(--txt-muted)] mr-1">stage:</span>
            {[
              ['ALL',           'all',           'text-cyan-sys'],
              ['early_up',      '↗ early',      'text-buy'],
              ['accelerating',  '⇈ accel',      'text-buy'],
              ['peak',          '▲ peak',       'text-hold'],
              ['slowing',       '→ slow',       'text-hold'],
              ['flat',          '· flat',       'text-[var(--txt-secondary)]'],
              ['fading',        '↘ fade',       'text-sell'],
              ['reversed',      '↓ rev',        'text-sell'],
            ].map(([key, label, color]) => (
              <button
                key={key}
                data-testid={`discovery-stage-filter-${key}`}
                onClick={() => setStageFilter(key)}
                className={`text-[9px] font-mono tracking-widest uppercase px-2 py-0.5 border transition-colors
                  ${stageFilter === key
                    ? 'bg-cyan-sys/10 border-cyan-sys ' + color
                    : color + ' border-[var(--border-muted)] hover:border-cyan-sys'}`}
              >
                {label}
              </button>
            ))}
          </div>
          {/* Mobile summary — shows active filters below the button */}
          {activeFilterCount > 0 && (
            <div className="md:hidden flex items-center gap-2 flex-wrap text-[9px] tracking-widest uppercase text-[var(--txt-muted)]"
                 data-testid="discovery-mobile-active-filters">
              {filter !== 'ALL' && <span className="text-cyan-sys">grade: {filter}</span>}
              {stageFilter !== 'ALL' && <span className="text-cyan-sys">stage: {stageFilter}</span>}
            </div>
          )}
        </div>

        {(cooldownLeft > 0 || cachedAge != null || warming) && (
          <div
            data-testid="discovery-status-banner"
            className={`mb-2 px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase border ${
              cooldownLeft > 0
                ? 'text-sell border-sell/40 bg-sell/5'
                : warming
                  ? 'text-hold border-hold/40 bg-hold/5'
                  : 'text-[var(--txt-muted)] border-[var(--border-muted)] bg-[var(--bg-secondary)]/40'
            }`}
          >
            {cooldownLeft > 0
              ? `// scan throttled · retry in ${cooldownLeft}s · scans still functional`
              : warming
                ? `// data engine warming up · ${warmCoverage}% of universe cached · more listings will appear as cache fills (auto-refresh every 5 min)`
                : `// served from cache · ${cachedAge}s old · refresh for live data`}
          </div>
        )}

        <div className="mobile-scroll" data-sticky-first="true">
          <table className="w-full text-[12px] font-mono min-w-[1000px]">
            <thead>
              <tr className="text-[10px] tracking-[0.25em] uppercase text-cyan-sys border-b border-[var(--border-default)]">
                <th className="text-left py-2 px-2">symbol</th>
                <th className="text-left py-2 px-2">grade</th>
                <th className="text-right py-2 px-2">price</th>
                <th className="text-right py-2 px-2">conf</th>
                <th className="text-right py-2 px-2" title="Momentum score (0-100) — RSI + MACD-histogram fused with confluence and regime alignment">momo</th>
                <th className="text-center py-2 px-2" title="Momentum lifecycle stage — early / accelerating / peak / slowing / fading / reversed. Hover for the reason list.">stage</th>
                <th className="text-left py-2 px-2">signal</th>
                <th className="text-left py-2 px-2">dir</th>
                <th className="text-left py-2 px-2">regime</th>
                <th className="text-right py-2 px-2">r:r</th>
                <th className="text-right py-2 px-2">stop</th>
                <th className="text-right py-2 px-2">target</th>
                <th className="text-center py-2 px-2">firewall</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && !loading && (
                <tr><td colSpan={12} className="text-center py-6 text-[var(--txt-muted)]">// no results</td></tr>
              )}
              {filtered.map((r) => {
                const sigC = r.signal === 'BUY' ? 'text-buy' : r.signal === 'SELL' ? 'text-sell' : 'text-hold';
                const dir = r.setup?.direction || (r.signal === 'SELL' ? 'SHORT' : 'LONG');
                const dirIsLong = dir === 'LONG';
                const stopPct = r.setup?.stop_pct;
                const tgtPct = r.setup?.target_pct;
                // ── Momentum score (0-100) — priority-1 component ──
                // Iter 84 baseline: confluence + regime-alignment bonus +
                // firewall penalty. Iter 85 adds a VW-ROC (volume-weighted
                // rate-of-change) tilt drawn from the backend's sub_scores:
                //   * volume  ≥ 5   → +5   (strong volume expansion)
                //   * volume  ≤ 1   → -5   (volume drying up)
                //   * momentum ≥ 30 → +5   (strong RSI+MACD tailwind)
                //   * momentum ≤ 15 → -5   (momentum fading)
                // Regime tokens fixed to match backend enum
                // (trending_up / trending_down, not UP / DOWN).
                const trend = r.regime?.trend;
                const aligned =
                  (r.signal === 'BUY'  && (trend === 'trending_up'   || trend === 'strong_up')) ||
                  (r.signal === 'SELL' && (trend === 'trending_down' || trend === 'strong_down'));
                const opposed =
                  (r.signal === 'BUY'  && (trend === 'trending_down' || trend === 'strong_down')) ||
                  (r.signal === 'SELL' && (trend === 'trending_up'   || trend === 'strong_up'));
                const base = Number(r.confluence || 0);
                const subVol = Number(r.sub_scores?.volume ?? 0);
                const subMom = Number(r.sub_scores?.momentum ?? 0);
                const vwBoost =
                  (subVol >= VW_VOL_HOT ? 5 : subVol <= VW_VOL_COLD ? -5 : 0) +
                  (subMom >= VW_MOM_HOT ? 5 : subMom <= VW_MOM_COLD ? -5 : 0);
                const momo = Math.max(0, Math.min(100, Math.round(
                  base +
                  (aligned ? 10 : 0) +
                  (opposed ? -12 : 0) +
                  (r.no_trade ? -18 : 0) +
                  vwBoost,
                )));
                const momoC = momo >= 70 ? 'text-buy' : momo >= 40 ? 'text-hold' : 'text-sell';
                // Divergence alert — regime + signal disagreement
                const divergent = opposed;
                // VW-ROC tooltip so operator can see how the boost was earned
                const vwLabel = (
                  (subVol >= VW_VOL_HOT ? 'vol+ ' : subVol <= VW_VOL_COLD ? 'vol- ' : '') +
                  (subMom >= VW_MOM_HOT ? 'mom+ ' : subMom <= VW_MOM_COLD ? 'mom- ' : '')
                ).trim() || 'neutral';
                return (
                  <tr key={r.symbol} data-testid={`discovery-row-${r.symbol}`} className="border-b border-[var(--border-default)] hover:bg-[#0c1012]">
                    <td className="py-2 px-2 font-bold">{r.symbol}</td>
                    <td className={`py-2 px-2 font-bold ${gradeColor(r.grade)}`}>{r.grade}</td>
                    <td className="py-2 px-2 text-right">{r.price}</td>
                    <td className="py-2 px-2 text-right font-bold text-cyan-sys">{r.confluence}</td>
                    <td className={`py-2 px-2 text-right font-bold ${momoC}`}
                        data-testid={`discovery-momo-${r.symbol}`}
                        title={`${aligned ? 'aligned +10' : opposed ? 'AGAINST regime -12' : 'neutral'} · VW-ROC: ${vwLabel} (${vwBoost >= 0 ? '+' : ''}${vwBoost})`}>
                      {momo}
                      {divergent && (
                        <span data-testid={`discovery-div-${r.symbol}`}
                              className="ml-1 inline-block px-1 py-0.5 text-[8px] tracking-widest uppercase border border-sell text-sell align-middle"
                              title="Divergence alert — signal opposes the current regime trend">
                          ÷
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-2 text-center align-middle" data-testid={`discovery-stage-${r.symbol}`}>
                      {(() => {
                        const lc = r.lifecycle || {};
                        const st = LIFECYCLE_STYLE[lc.stage] || LIFECYCLE_STYLE.unknown;
                        const conf = lc.confidence ?? 0;
                        const reasonList = Array.isArray(lc.reasons) ? lc.reasons.join(' · ') : '';
                        const title = `${lc.label || 'unknown'} · confidence ${conf}${reasonList ? ' · ' + reasonList : ''}`;
                        return (
                          <span className={`inline-flex flex-col items-center gap-0.5 text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5 border ${st.color} ${st.border}`}
                                title={title}>
                            <span>{st.glyph}</span>
                            {conf > 0 && <span className="text-[8px] opacity-70 font-mono">{conf}</span>}
                          </span>
                        );
                      })()}
                    </td>
                    <td className={`py-2 px-2 ${sigC}`}>{r.signal}</td>
                    <td className={`py-2 px-2 text-[10px] tracking-wider ${dirIsLong ? 'text-buy' : 'text-sell'}`}
                        title={dirIsLong ? 'long setup — stop below, target above' : 'short setup — stop above, target below'}>
                      {dirIsLong ? '▲ LONG' : '▼ SHORT'}
                    </td>
                    <td className="py-2 px-2 text-[var(--txt-secondary)] text-[11px]">{r.regime?.trend}/{r.regime?.volatility}</td>
                    <td className={`py-2 px-2 text-right ${r.setup?.r_r >= 2 ? 'text-buy' : 'text-hold'}`}>{r.setup?.r_r}</td>
                    <td className="py-2 px-2 text-right text-sell"
                        title={`stop-loss · ${stopPct != null ? (stopPct > 0 ? '+' : '') + stopPct + '%' : ''}`}>
                      {r.setup?.stop}
                      {stopPct != null && <span className="block text-[9px] text-[var(--txt-muted)]">{stopPct > 0 ? '+' : ''}{stopPct}%</span>}
                    </td>
                    <td className="py-2 px-2 text-right text-buy"
                        title={`profit-target · ${tgtPct != null ? (tgtPct > 0 ? '+' : '') + tgtPct + '%' : ''}`}>
                      {r.setup?.target}
                      {tgtPct != null && <span className="block text-[9px] text-[var(--txt-muted)]">{tgtPct > 0 ? '+' : ''}{tgtPct}%</span>}
                    </td>
                    <td className="py-2 px-2 text-center">
                      {r.no_trade
                        ? <span className="inline-flex items-center gap-1 text-sell text-[10px]"><ShieldOff size={10}/> HOLD</span>
                        : <span className="inline-flex items-center gap-1 text-buy text-[10px]"><TrendingUp size={10}/> CLEAR</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CornerPanel>
      <MobileSheet
        open={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        title="// Discovery Filters"
        testId="discovery-mobile-filter-sheet"
      >
        {filterChipsSheet}
      </MobileSheet>
    </div>
  );
}
